import {
    inject,
    Injectable,
    NgZone,
    type WritableSignal,
} from '@angular/core';

/**
 * The state of the section loaders (a spinner over a part of a screen, found by name: `itemsCategoryLoader`,
 * `stockMovementsLoader`...). `SectionLoaderComponent` registers one state per instance on the screen and this service
 * switches them by name: an instance only hears what happens while it is on the screen, and starts hidden, like the
 * AngularJS `loader` directive it replaces (which listened to the `loader:changeStatus` broadcast).
 */
@Injectable({
    providedIn: 'root',
})
export class SectionLoaderService {
    private readonly zone = inject(NgZone);
    private readonly instances = new Map<string, Set<WritableSignal<boolean>>>();

    /** Called by a loader that appears on the screen. Returns what it must call when it goes away. */
    public register(name: string, enabled: WritableSignal<boolean>): () => void {
        const instances = this.instances.get(name) ?? new Set<WritableSignal<boolean>>();

        instances.add(enabled);
        this.instances.set(name, instances);

        return () => {
            instances.delete(enabled);

            if (instances.size === 0 && this.instances.get(name) === instances) {
                this.instances.delete(name);
            }
        };
    }

    public show(name: string): void {
        this.set(name, true);
    }

    public hide(name: string): void {
        this.set(name, false);
    }

    /** Runs in the Angular zone: the AngularJS broadcast is not always inside it, and Angular 17 ignores signals changed outside. */
    public set(name: string, enabled: boolean): void {
        this.zone.run(() => this.instances.get(name)?.forEach((instance) => instance.set(enabled)));
    }
}
